import { Controller } from "@hotwired/stimulus"

const AVG_MONTHS_RETAINED = 14
const CHURN_REDUCTION = 2

export default class extends Controller {
  static targets = [
    "membersSlider",
    "membersInput",
    "priceInput",
    "churnSlider",
    "churnInput",
    "membersLost",
    "monthlyRevenueLost",
    "annualRevenueLost",
    "ltvLost",
    "savedMembers",
    "savedMonthlyRevenue",
    "savedAnnualRevenue",
    "reducedChurnRate"
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const members = this.parseNumber(this.membersInputTarget.value) || 0
    const price = this.parseCurrency(this.priceInputTarget.value) || 0
    const churnRate = this.parseNumber(this.churnInputTarget.value) || 0

    // Sync sliders with inputs
    if (this.hasMembersSliderTarget) this.syncSlider(this.membersSliderTarget, members)
    if (this.hasChurnSliderTarget) this.syncSlider(this.churnSliderTarget, churnRate)

    // Current losses
    const membersLost = Math.round(members * (churnRate / 100))
    const monthlyRevenueLost = membersLost * price
    const annualRevenueLost = monthlyRevenueLost * 12
    const ltvLost = price * AVG_MONTHS_RETAINED

    // Savings if churn reduced by 2%
    const reducedRate = Math.max(0, churnRate - CHURN_REDUCTION)
    const membersLostReduced = Math.round(members * (reducedRate / 100))
    const savedMembers = membersLost - membersLostReduced
    const savedMonthlyRevenue = savedMembers * price
    const savedAnnualRevenue = savedMonthlyRevenue * 12

    // Update DOM
    this.membersLostTarget.textContent = this.formatNumber(membersLost)
    this.monthlyRevenueLostTarget.textContent = this.formatCurrency(monthlyRevenueLost)
    this.annualRevenueLostTarget.textContent = this.formatCurrency(annualRevenueLost)
    this.ltvLostTarget.textContent = this.formatCurrency(ltvLost)

    this.savedMembersTarget.textContent = this.formatNumber(savedMembers)
    this.savedMonthlyRevenueTarget.textContent = this.formatCurrency(savedMonthlyRevenue)
    this.savedAnnualRevenueTarget.textContent = this.formatCurrency(savedAnnualRevenue)
    this.reducedChurnRateTarget.textContent = `${reducedRate}%`
  }

  membersSliderInput(event) {
    const v = parseInt(event.target.value, 10)
    if (!Number.isFinite(v)) return
    this.membersInputTarget.value = v
    this.calculate()
  }

  churnSliderInput(event) {
    const v = parseInt(event.target.value, 10)
    if (!Number.isFinite(v)) return
    this.churnInputTarget.value = v
    this.calculate()
  }

  inputChanged() {
    this.calculate()
  }

  parseNumber(raw) {
    if (raw == null || String(raw).trim() === "") return 0
    const cleaned = String(raw).replace(/[^0-9.]/g, "")
    const n = parseFloat(cleaned)
    return Number.isFinite(n) ? n : 0
  }

  parseCurrency(raw) {
    if (raw == null || String(raw).trim() === "") return 0
    const cleaned = String(raw).replace(/[^0-9.]/g, "")
    const n = parseFloat(cleaned)
    return Number.isFinite(n) ? n : 0
  }

  formatNumber(n) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n)
  }

  formatCurrency(n) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0
    }).format(n)
  }

  syncSlider(el, value) {
    if (!el) return
    const s = String(value)
    if (el.value !== s) el.value = s
  }
}
